/**
 * Configuration de l'écran Roxzone Display (sponsors + carousel).
 *
 * Écrit sur le cloud-api (`/display/config/{event_id}`) : c'est la source de
 * vérité de l'écran servi EN LIGNE par le scoring service. Un événement scoré
 * en local lit sa propre config sur l'API locale (inchangé).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoSave } from '../hooks/useAutoSave';
import AutoSaveIndicator from '../components/ui/AutoSaveIndicator';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { NumberInput } from '../components/ui/number-input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { cn } from '../lib/utils';
import { useEvent } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api.service';
import { DisplayConfig } from '../types';

const SPONSOR_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
const SPONSOR_MAX_BYTES = 5 * 1024 * 1024;

/** Les fichiers de l'API sont renvoyés en URL relative → on la préfixe ici. */
const getApiFileUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (!url.startsWith('/api')) return url;
  const base = (process.env.REACT_APP_API_URL || 'https://events.logfit.eu/api/v1').replace('/api/v1', '');
  return `${base}${url}`;
};

const DisplaySettings: React.FC = () => {
  const { t } = useTranslation();
  const { selectedEventId } = useEvent();
  // L'overlay OBS est un service opéré par LogFit, pas une fonctionnalité
  // libre-service : ses réglages (et jusqu'à son existence) restent réservés
  // aux comptes admin — un organisateur ne doit pas le découvrir ici.
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [config, setConfig] = useState<DisplayConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // `load` est défini avant le hook : la ref lui donne accès à `setBaseline`
  // sans réordonner le fichier ni recréer `load` à chaque rendu.
  const baselineRef = useRef<((v: Record<string, any>) => void) | null>(null);

  const load = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);
    try {
      const res = await apiService.get(`/display/config/${selectedEventId}`);
      setConfig(res.data);
      // Ce que le serveur porte : `commitIfChanged` s'y compare pour ne pas
      // réécrire une durée qu'on a seulement traversée au clavier.
      baselineRef.current?.({
        carousel_seconds: res.data?.carousel_seconds,
        overlay_gap_seconds: res.data?.overlay_gap_seconds,
        sponsor_frequency: res.data?.sponsor_frequency,
      });
    } catch (e: any) {
      setConfig(null);
      toast.error(e?.response?.data?.detail || t('displaySettings.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, t]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * L'écran s'enregistre au fur et à mesure : l'interrupteur écrit tout de
   * suite, les durées au blur. Le `PUT` est PARTIEL — on n'envoie que le champ
   * touché, donc le réglage admin qu'un organisateur ne voit même pas ne peut
   * plus être renvoyé par erreur, et une durée en cours de frappe n'écrase
   * rien. Les logos, eux, s'écrivaient déjà immédiatement.
   */
  const auto = useAutoSave<Record<string, any>>(useCallback(async (patch) => {
    if (!selectedEventId) return;
    const res = await apiService.put(`/display/config/${selectedEventId}`, patch);
    // ⚠️ On ne réinjecte QUE les logos : remplacer la config complète par la
    // réponse écraserait un champ voisin en cours de saisie.
    setConfig(c => (c ? { ...c, sponsor_logos: res.data?.sponsor_logos ?? c.sponsor_logos } : c));
  }, [selectedEventId]), {
    onError: (e: any) => toast.error(e?.response?.data?.detail || t('displaySettings.saveError')),
  });
  baselineRef.current = auto.setBaseline;

  // Les logos s'enregistrent immédiatement (comme le logo d'événement) : le
  // bouton « Enregistrer » ne porte que le toggle et les deux durées.
  //
  // ⚠️ On ne réinjecte QUE `sponsor_logos` : remplacer la config complète
  // renvoyée par le serveur écraserait les réglages en cours de saisie (toggle,
  // durées) par leur valeur encore en base — le toggle qu'on vient d'activer
  // pour révéler ce champ retomberait à false sous les doigts.
  const uploadLogos = async (files: File[]) => {
    if (!selectedEventId || files.length === 0) return;
    for (const f of files) {
      if (!SPONSOR_TYPES.includes(f.type)) {
        toast.error(`${f.name} : ${t('displaySettings.invalidType')}`);
        return;
      }
      if (f.size > SPONSOR_MAX_BYTES) {
        toast.error(`${f.name} : ${t('displaySettings.tooLarge')}`);
        return;
      }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      // Dépôt multiple : le champ `files` est répété, l'API les ajoute à la suite.
      files.forEach((f) => fd.append('files', f));
      const res = await apiService.post(`/display/sponsor-logos/${selectedEventId}`, fd);
      setConfig((prev) => (prev ? { ...prev, sponsor_logos: res.data.sponsor_logos } : prev));
      toast.success(t('displaySettings.logosAdded', { count: files.length }));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('displaySettings.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await uploadLogos(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    await uploadLogos(Array.from(e.dataTransfer.files ?? []));
  };

  const handleDeleteLogo = async (logoId: string) => {
    if (!selectedEventId) return;
    setUploading(true);
    try {
      const res = await apiService.delete(`/display/sponsor-logos/${selectedEventId}/${logoId}`);
      setConfig((prev) => (prev ? { ...prev, sponsor_logos: res.data.sponsor_logos } : prev));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('displaySettings.deleteError'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-3xl">
      <p className="text-sm text-muted-foreground">{t('displaySettings.intro')}</p>

      <Card>
        <CardContent className="pt-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
          ) : !selectedEventId ? (
            <div className="text-center py-6 text-muted-foreground text-sm">
              {t('displaySettings.noEvent')}
            </div>
          ) : !config ? (
            <div className="text-center py-6 text-muted-foreground text-sm">
              {t('displaySettings.loadError')}
            </div>
          ) : (
            <>
              {/* Durée d'une page de classement */}
              <div>
                <Label htmlFor="carousel_seconds">{t('displaySettings.carouselSeconds')}</Label>
                <NumberInput
                  id="carousel_seconds"
                  min={3}
                  max={300}
                  className="mt-1 w-32"
                  value={config.carousel_seconds}
                  onChange={(e) => setConfig({ ...config, carousel_seconds: Number(e.target.value) })}
                  onBlur={(e) => void auto.commitIfChanged('carousel_seconds', Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('displaySettings.carouselSecondsHint')}
                </p>
              </div>

              {/* Overlay OBS : cadence propre au mode incrustation. Sur la TV le
                  classement est permanent, ce réglage n'y a pas de sens.
                  RÉSERVÉ AUX ADMINS : l'overlay est un service opéré par LogFit,
                  il ne doit pas apparaître chez l'organisateur. */}
              {isAdmin && (
              <div className="border-t pt-4">
                <Label htmlFor="overlay_gap_seconds">{t('displaySettings.overlayGap')}</Label>
                <NumberInput
                  id="overlay_gap_seconds"
                  min={0}
                  max={3600}
                  className="mt-1 w-32"
                  value={config.overlay_gap_seconds}
                  onChange={(e) => setConfig({ ...config, overlay_gap_seconds: Number(e.target.value) })}
                  onBlur={(e) => void auto.commitIfChanged('overlay_gap_seconds', Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('displaySettings.overlayGapHint')}
                </p>
              </div>
              )}

              {/* Page sponsors */}
              <div className="border-t pt-4 flex items-center justify-between gap-4">
                <div>
                  <Label>{t('displaySettings.sponsorEnabled')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('displaySettings.sponsorEnabledHint')}
                  </p>
                </div>
                <Switch
                  checked={config.sponsor_enabled}
                  onCheckedChange={(checked: boolean) => {
                    setConfig({ ...config, sponsor_enabled: checked });
                    void auto.commit({ sponsor_enabled: checked },
                      () => setConfig(c => (c ? { ...c, sponsor_enabled: !checked } : c)));
                  }}
                />
              </div>

              {config.sponsor_enabled && (
                <>
                  <div>
                    <Label htmlFor="sponsor_frequency">{t('displaySettings.sponsorFrequency')}</Label>
                    <NumberInput
                      id="sponsor_frequency"
                      min={1}
                      max={50}
                      className="mt-1 w-32"
                      value={config.sponsor_frequency}
                      onChange={(e) => setConfig({ ...config, sponsor_frequency: Number(e.target.value) })}
                  onBlur={(e) => void auto.commitIfChanged('sponsor_frequency', Number(e.target.value))}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('displaySettings.sponsorFrequencyHint')}
                    </p>
                  </div>

                  {/* Image : drag & drop ou sélecteur — enregistrée immédiatement */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    className={cn(
                      'rounded-md border border-dashed p-4 transition-colors',
                      dragOver ? 'border-white/40 bg-white/[0.05]' : 'border-muted-foreground/30'
                    )}
                  >
                    <p className="text-xs text-muted-foreground mb-1">
                      {t('displaySettings.dropHint')}
                    </p>
                    {/* L'écran pose les logos sur une plaque de verre : un fond
                        opaque ne laisse rien voir du décor derrière. */}
                    <p className="text-xs text-foreground/90 mb-3">
                      {t('displaySettings.transparentHint')}
                    </p>

                    {/* Galerie : chaque logo est une tuile, comme à l'écran. */}
                    {config.sponsor_logos.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                        {config.sponsor_logos.map((logo) => (
                          <div
                            key={logo.id}
                            className="group relative aspect-[3/2] rounded-md bg-white p-2 flex items-center justify-center"
                          >
                            <img
                              src={getApiFileUrl(logo.url) || ''}
                              alt=""
                              className="max-h-full max-w-full object-contain"
                            />
                            <button
                              type="button"
                              disabled={uploading}
                              onClick={() => handleDeleteLogo(logo.id)}
                              title={t('displaySettings.removeLogo')}
                              className="absolute -top-2 -right-2 rounded-full bg-background border p-1
                                         text-muted-foreground [@media(hover:hover)]:opacity-0 group-hover:opacity-100
                                         focus-visible:opacity-100 hover:text-destructive transition-opacity"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer inline-flex items-center px-3 py-2 rounded-md border text-sm hover:bg-muted">
                        {uploading ? <Loader2 className="animate-spin" size={16} /> : t('displaySettings.chooseLogos')}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          multiple
                          className="hidden"
                          disabled={uploading}
                          onChange={handleFileInput}
                        />
                      </label>
                      <span className="text-xs text-muted-foreground">
                        {t('displaySettings.logoCount', { count: config.sponsor_logos.length })}
                      </span>
                    </div>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between gap-3 border-t pt-4">
                <span className="text-xs text-muted-foreground">{t('autoSave.hint')}</span>
                <AutoSaveIndicator status={auto.status} retry={auto.retry} />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DisplaySettings;
